import React, { useEffect, useState } from "react";
import api, { formatApiError } from "@/api/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Users, Plus, Edit, Trash2, ToggleLeft, ToggleRight, ExternalLink, Shield } from "lucide-react";
import { toast } from "sonner";

const PERMS = [
  ["view_invoices", "Näe laskut"],
  ["download_invoices", "Lataa laskut"],
  ["view_documents", "Näe dokumentit"],
  ["submit_request", "Tee tukipyyntöjä"],
  ["pay_online", "Maksa verkossa"],
];

export default function CustomerPortalDashboard() {
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/portal/access");
      setList(r.data || []);
    } catch (e) { toast.error(formatApiError(e)); }
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const save = async () => {
    try {
      const data = { ...form };
      if (form.id) await api.put(`/portal/access/${form.id}`, data);
      else await api.post("/portal/access", data);
      toast.success("Tallennettu");
      setForm(null); load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const toggle = async (it) => {
    try { await api.put(`/portal/access/${it.id}`, { ...it, is_active: !it.is_active }); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  const remove = async (id) => {
    if (!window.confirm("Poistetaanko pääsy?")) return;
    try { await api.delete(`/portal/access/${id}`); toast.success("Poistettu"); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="space-y-6" data-testid="customer-portal-page">
      <header className="flex justify-between items-end flex-wrap gap-4">
        <div>
          <p className="text-[10px] uppercase tracking-[0.3em] text-white/40">Asiakasportaali</p>
          <h1 className="text-3xl font-display gradient-text-luxe">Asiakaspääsy & oikeudet</h1>
          <p className="text-white/60 text-sm mt-1">Anna asiakkaillesi pääsy laskuihin, dokumentteihin ja tukipyyntöihin.</p>
        </div>
        <Button onClick={() => setForm({ is_active: true, permissions: ["view_invoices"] })} className="btn-luxe" data-testid="add-portal-access">
          <Plus className="w-4 h-4 mr-1" /> Anna pääsy
        </Button>
      </header>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {loading ? <p className="text-white/40">Ladataan…</p> :
         list.length === 0 ? <p className="text-white/40">Ei asiakaspääsyjä</p> :
         list.map((it) => (
          <div key={it.id} className="glass-card p-5" data-testid={`portal-${it.id}`}>
            <div className="flex items-start justify-between relative z-10">
              <div className="min-w-0">
                <h3 className="font-display text-white truncate">{it.customer_name}</h3>
                <p className="text-xs text-white/50 truncate">{it.customer_email}</p>
              </div>
              <Badge className={`text-[10px] border-0 ${it.is_active ? "bg-emerald-500/20 text-emerald-200" : "bg-zinc-500/20 text-zinc-200"}`}>
                {it.is_active ? "Aktiivinen" : "Pois"}
              </Badge>
            </div>
            <div className="mt-3 flex flex-wrap gap-1 relative z-10">
              {(it.permissions || []).map(p => {
                const m = PERMS.find(x => x[0] === p);
                return <Badge key={p} className="text-[9px] bg-amber-500/15 text-amber-200 border-0">{m ? m[1] : p}</Badge>;
              })}
            </div>
            <div className="flex gap-1 mt-4 relative z-10">
              <Button size="sm" variant="ghost" onClick={() => toggle(it)} className="text-xs flex-1">
                {it.is_active ? <ToggleRight className="w-3 h-3 text-emerald-400" /> : <ToggleLeft className="w-3 h-3 text-zinc-400" />}
              </Button>
              <Button size="sm" variant="ghost" onClick={() => setForm(it)} className="text-xs flex-1"><Edit className="w-3 h-3" /></Button>
              <Button size="sm" variant="ghost" onClick={() => remove(it.id)} className="text-xs text-red-300"><Trash2 className="w-3 h-3" /></Button>
            </div>
          </div>
        ))}
      </div>

      <Dialog open={!!form} onOpenChange={(v) => !v && setForm(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle>{form?.id ? "Muokkaa pääsyä" : "Uusi asiakaspääsy"}</DialogTitle></DialogHeader>
          {form && (
            <div className="space-y-3">
              <div><Label>Asiakkaan nimi</Label><Input value={form.customer_name || ""} onChange={(e) => setForm({ ...form, customer_name: e.target.value })} /></div>
              <div><Label>Sähköposti</Label><Input value={form.customer_email || ""} onChange={(e) => setForm({ ...form, customer_email: e.target.value })} /></div>
              <div><Label>Asiakas-ID</Label><Input value={form.customer_id || ""} onChange={(e) => setForm({ ...form, customer_id: e.target.value })} placeholder="Liitä CRM-asiakkaaseen" /></div>
              <div>
                <Label>Oikeudet</Label>
                <div className="grid grid-cols-2 gap-1 mt-1">
                  {PERMS.map(([k, l]) => (
                    <label key={k} className="flex items-center gap-2 text-xs text-white/80">
                      <input type="checkbox" checked={(form.permissions || []).includes(k)} onChange={(e) => {
                        const p = new Set(form.permissions || []);
                        if (e.target.checked) p.add(k); else p.delete(k);
                        setForm({ ...form, permissions: Array.from(p) });
                      }} />
                      {l}
                    </label>
                  ))}
                </div>
              </div>
              <div className="flex gap-2 pt-2">
                <Button variant="ghost" onClick={() => setForm(null)} className="flex-1">Peruuta</Button>
                <Button onClick={save} className="btn-luxe flex-1">Tallenna</Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
